import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import { queryCountSessionsTrashed } from '../utils/directFetch';

const SessionTrashPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recoveringId, setRecoveringId] = useState(null);
  const [emptyingTrash, setEmptyingTrash] = useState(false);

  useEffect(() => {
    loadTrashedSessions();
  }, []);

  const loadTrashedSessions = async () => {
    setLoading(true);
    try {
      const storedStoreId = localStorage.getItem('selectedStoreId');
      if (!storedStoreId) return;
      const storeId = parseInt(storedStoreId);

      const { data, error } = await queryCountSessionsTrashed(storeId);
      if (error) {
        console.error('Error loading trashed sessions:', error);
        return;
      }

      // Group by count_group_id so stacks stay together
      const grouped = {};
      (data || []).forEach(s => {
        const gid = s.count_group_id || s.id;
        if (!grouped[gid]) grouped[gid] = [];
        grouped[gid].push(s);
      });

      const enriched = (data || []).map(s => {
        const gid = s.count_group_id || s.id;
        return { ...s, stackSize: grouped[gid].length, stackSessions: grouped[gid] };
      });

      setSessions(enriched);
    } catch (err) {
      console.error('Error loading trashed sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (session) => {
    setRecoveringId(session.id);
    try {
      const stackSessions = session.stackSessions || [session];
      const isStack = stackSessions.length > 1;

      let sessionIds = [session.id];

      if (isStack) {
        const recoverAll = window.confirm(
          `"${session.name}" is part of a stack of ${stackSessions.length} trashed counts.\n\nRecover the entire stack?\n\nClick OK for entire stack, Cancel for just this count.`
        );
        if (recoverAll) {
          sessionIds = stackSessions.map(s => s.id);
        }
      }

      for (const id of sessionIds) {
        const { error } = await supabase
          .from('count_sessions')
          .update({ is_trashed: false, trashed_at: null })
          .eq('id', id);
        if (error) throw error;
      }

      await loadTrashedSessions();
    } catch (err) {
      console.error('Error recovering session:', err);
      alert('Error recovering session: ' + err.message);
    } finally {
      setRecoveringId(null);
    }
  };

  const handleEmptyTrash = async () => {
    const deletable = sessions.filter(s => s.status !== 'completed');
    const completedCount = sessions.length - deletable.length;

    if (deletable.length === 0) {
      alert(
        `The trash contains only completed count sessions (${completedCount}), which cannot be permanently deleted.\n\nCompleted sessions can be archived or voided from the Archive view.`
      );
      return;
    }

    let confirmMsg = `Are you sure you want to permanently delete ${deletable.length} trashed count(s)?\n\nThis will remove all counted data from the system. This action CANNOT be undone.`;
    if (completedCount > 0) {
      confirmMsg += `\n\nNote: ${completedCount} completed count(s) in the trash will be skipped — they cannot be permanently deleted.`;
    }

    const firstConfirm = window.confirm(confirmMsg);
    if (!firstConfirm) return;

    const secondConfirm = window.confirm(
      'FINAL WARNING: The selected count data will be permanently removed from the database.\n\nClick OK to confirm.'
    );
    if (!secondConfirm) return;

    setEmptyingTrash(true);
    try {
      const sessionIds = deletable.map(s => s.id);

      // Delete count items first
      for (const id of sessionIds) {
        try {
          await supabase.from('count_items').delete().eq('count_session_id', id);
        } catch (e) {
          console.warn('count_items delete skipped (CASCADE will handle):', e.message);
        }
      }

      // Delete the sessions
      for (const id of sessionIds) {
        const { error } = await supabase.from('count_sessions').delete().eq('id', id);
        if (error) {
          console.error('Error permanently deleting session:', id, error);
        }
      }

      if (completedCount > 0) {
        alert(`Done. ${deletable.length} session(s) deleted. ${completedCount} completed session(s) were skipped and remain in the trash.`);
      }

      await loadTrashedSessions();
    } catch (err) {
      console.error('Error emptying trash:', err);
      alert('Error emptying trash: ' + err.message);
    } finally {
      setEmptyingTrash(false);
    }
  };

  const handlePermanentDelete = async (session) => {
    if (session.status === 'completed') {
      alert(
        `"${session.name || 'This count'}" is a completed session and cannot be permanently deleted.\n\nCompleted sessions preserve your audit trail. Use Archive to hide it from the main view, or Void to exclude it from reports while keeping the record.`
      );
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete "${session.name}"?\n\nThis will remove all counted data from reports and the database. This action CANNOT be undone.`
    );
    if (!confirmed) return;

    try {
      // Delete items first
      try {
        await supabase.from('count_items').delete().eq('count_session_id', session.id);
      } catch (e) {
        console.warn('count_items delete skipped:', e.message);
      }

      const { error } = await supabase.from('count_sessions').delete().eq('id', session.id);
      if (error) throw error;

      await loadTrashedSessions();
    } catch (err) {
      console.error('Error permanently deleting session:', err);
      alert('Error: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="session-page-scroll" style={{
      minHeight: '100vh',
      backgroundColor: '#FEFCFA',
      color: '#5A4A42',
      padding: '24px',
      fontFamily: "'Open Sans', sans-serif"
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ color: '#333', margin: '0 0 4px', fontWeight: '700', fontSize: '1.4rem' }}>
              🗑️ Trash
            </h2>
            <p style={{ color: '#777', margin: 0, fontSize: '0.85rem' }}>
              Trashed counts are removed from reports. Recover them, or permanently delete to free space.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {sessions.length > 0 && (
              <button
                onClick={handleEmptyTrash}
                disabled={emptyingTrash}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#d32f2f',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  cursor: emptyingTrash ? 'wait' : 'pointer',
                  opacity: emptyingTrash ? 0.5 : 1
                }}
              >
                {emptyingTrash ? 'Emptying...' : `🗑️ Empty Trash (${sessions.length})`}
              </button>
            )}
            <a
              href="/count.html?view=overview"
              style={{
                padding: '8px 16px',
                backgroundColor: '#e0e0e0',
                border: '1px solid #ccc',
                borderRadius: '8px',
                color: '#555',
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}
            >
              ← Back to Counts
            </a>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner-border" role="status" style={{ color: '#999' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '12px', color: '#999' }}>Loading trash...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '64px 24px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #ddd'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.3 }}>🗑️</div>
            <p style={{ color: '#888', fontSize: '1rem' }}>Trash is empty.</p>
            <p style={{ color: '#aaa', fontSize: '0.85rem' }}>Deleted counts will appear here before permanent removal.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {sessions.map(session => {
              const isRecovering = recoveringId === session.id;
              const isCompleted = session.status === 'completed';
              return (
                <div key={session.id} style={{
                  backgroundColor: '#fff',
                  border: isCompleted ? '1px solid #c0b8b0' : '1px solid #ddd',
                  borderRadius: '10px',
                  padding: '14px',
                  opacity: 0.75
                }}>
                  {/* Card header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        color: '#555',
                        fontWeight: '700',
                        fontSize: '0.9rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textDecoration: 'line-through'
                      }}>
                        {session.name}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {isCompleted && (
                        <span style={{
                          backgroundColor: '#e8f0e8',
                          color: '#4a7a4a',
                          fontSize: '0.6rem',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          Completed
                        </span>
                      )}
                      <span style={{
                        backgroundColor: '#eee',
                        color: '#999',
                        fontSize: '0.6rem',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        Trashed
                      </span>
                    </div>
                  </div>

                  {/* Card details */}
                  <div style={{ fontSize: '0.75rem', color: '#888', lineHeight: '1.6' }}>
                    <div>📅 {formatDate(session.session_date)}</div>
                    <div>📊 {(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items counted` : c > 0 ? `${c} items counted` : '0 items counted'; })()}</div>
                    <div>🗑️ Trashed: {formatDate(session.trashed_at)}</div>
                    {session.stackSize > 1 && (
                      <div>🔗 Part of stack ({session.stackSize} counts)</div>
                    )}
                    {isCompleted && (
                      <div style={{ color: '#8b7060', fontStyle: 'italic', marginTop: '2px' }}>
                        🔒 Completed counts cannot be permanently deleted — recover to archive or void
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => handleRecover(session)}
                      disabled={isRecovering}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        backgroundColor: '#e0e0e0',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        color: '#555',
                        fontSize: '0.78rem',
                        fontWeight: '600',
                        cursor: isRecovering ? 'wait' : 'pointer',
                        opacity: isRecovering ? 0.5 : 1
                      }}
                    >
                      {isRecovering ? '...' : '↩️ Recover'}
                    </button>
                    {!isCompleted && (
                      <button
                        onClick={() => handlePermanentDelete(session)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#d32f2f',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.78rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        ✕ Delete Forever
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionTrashPage;
